import React, { useState, useEffect, useCallback } from 'react';
import {
  demoRequestService,
  DemoRequestItem,
  DemoRequestStats,
  DemoRequestStatus,
} from '../../services/demoRequestService';

const STATUS_CONFIG: Record<
  DemoRequestStatus,
  { label: string; badge: string; dot: string }
> = {
  new: {
    label: 'New',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
  },
  contacted: {
    label: 'Contacted',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
  },
  scheduled: {
    label: 'Demo Scheduled',
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    dot: 'bg-purple-500',
  },
  completed: {
    label: 'Completed',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
  },
  archived: {
    label: 'Archived',
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    dot: 'bg-slate-400',
  },
};

const DemoRequestManagement: React.FC = () => {
  const [items, setItems] = useState<DemoRequestItem[]>([]);
  const [stats, setStats] = useState<DemoRequestStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Status updating state
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  // Selected lead modal
  const [selectedLead, setSelectedLead] = useState<DemoRequestItem | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await demoRequestService.getDemoRequests({
        page,
        per_page: 20,
        status: statusFilter,
        search,
      });
      setItems(data.items);
      setStats(data.stats);
      setTotalPages(data.pages);
      setTotalCount(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  const handleStatusChange = async (item: DemoRequestItem, newStatus: DemoRequestStatus) => {
    if (item.status === newStatus) return;
    setUpdatingId(item.id);
    try {
      const updated = await demoRequestService.updateStatus(item.id, newStatus);
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
      if (selectedLead && selectedLead.id === item.id) {
        setSelectedLead(updated);
      }
      // Reload stats in background
      const fresh = await demoRequestService.getDemoRequests({
        page,
        per_page: 20,
        status: statusFilter,
        search,
      });
      setStats(fresh.stats);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      await demoRequestService.downloadCsv(statusFilter, search);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to download CSV');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (isoStr: string) => {
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Inbound Demo Leads</h1>
          <p className="text-sm text-slate-500">
            Prospects who submitted walkthrough requests for Flash Prepared exercises, academy, and BCP.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-xs hover:bg-slate-50 disabled:opacity-50"
          >
            <svg
              className={`mr-1.5 h-3.5 w-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>Refresh</span>
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting || items.length === 0}
            className="inline-flex items-center rounded-lg bg-[#991B1B] px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#7F1D1D] disabled:opacity-50"
          >
            <svg className="mr-1.5 h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            <span>{exporting ? 'Exporting...' : 'Export CSV'}</span>
          </button>
        </div>
      </div>

      {/* Aggregate Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Leads</div>
            <div className="mt-1 text-2xl font-bold text-slate-900">{stats.total}</div>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 shadow-xs">
            <div className="text-xs font-medium text-blue-700 uppercase tracking-wider">New Leads</div>
            <div className="mt-1 text-2xl font-bold text-blue-900">{stats.new}</div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 shadow-xs">
            <div className="text-xs font-medium text-amber-700 uppercase tracking-wider">Contacted</div>
            <div className="mt-1 text-2xl font-bold text-amber-900">{stats.contacted}</div>
          </div>
          <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 shadow-xs">
            <div className="text-xs font-medium text-purple-700 uppercase tracking-wider">Scheduled</div>
            <div className="mt-1 text-2xl font-bold text-purple-900">{stats.scheduled}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 shadow-xs col-span-2 sm:col-span-1">
            <div className="text-xs font-medium text-emerald-700 uppercase tracking-wider">Completed</div>
            <div className="mt-1 text-2xl font-bold text-emerald-900">{stats.completed}</div>
          </div>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-slate-200 bg-white p-3 shadow-xs">
        {/* Status Filter Buttons */}
        <div className="flex flex-wrap items-center gap-1.5">
          {[
            { key: 'all', label: 'All' },
            { key: 'new', label: 'New' },
            { key: 'contacted', label: 'Contacted' },
            { key: 'scheduled', label: 'Scheduled' },
            { key: 'completed', label: 'Completed' },
            { key: 'archived', label: 'Archived' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setStatusFilter(tab.key);
                setPage(1);
              }}
              className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                statusFilter === tab.key
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, org, email..."
              className="w-56 sm:w-64 rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none"
            />
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
              <svg className="h-3.5 w-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <button
            type="submit"
            className="rounded-lg border border-slate-300 bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
          >
            Filter
          </button>
        </form>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {error}
        </div>
      )}

      {/* Main Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading demo requests...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="mx-auto h-10 w-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="mt-2 text-sm font-semibold text-slate-900">No demo requests found</h3>
            <p className="mt-1 text-xs text-slate-500">
              {search || statusFilter !== 'all'
                ? 'Try adjusting your search terms or status filter.'
                : 'Demo requests filled out on the public website will appear here.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">Prospect</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Organization</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Interests</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Notes</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Received</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Status</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {items.map((item) => {
                  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.new;
                  return (
                    <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3.5">
                        <div className="font-semibold text-slate-900">{item.name}</div>
                        <a
                          href={`mailto:${item.email}?subject=Flash Prepared Demo Walkthrough`}
                          className="text-slate-500 hover:text-indigo-600 underline decoration-dotted"
                          title="Send email"
                        >
                          {item.email}
                        </a>
                        <div className="text-[11px] text-slate-400">{item.role}</div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="font-medium text-slate-900">{item.organization}</div>
                        <div className="text-[11px] text-slate-400">{item.organization_size}</div>
                      </td>
                      <td className="px-4 py-3.5 max-w-xs">
                        <div className="flex flex-wrap gap-1">
                          {item.interests && item.interests.length > 0 ? (
                            item.interests.map((interest, idx) => (
                              <span
                                key={idx}
                                className="inline-block rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
                              >
                                {interest}
                              </span>
                            ))
                          ) : (
                            <span className="text-slate-400 text-[11px]">General Demo</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 max-w-xs truncate text-slate-600" title={item.notes || ''}>
                        {item.notes ? (
                          <span
                            onClick={() => setSelectedLead(item)}
                            className="cursor-pointer hover:text-indigo-600 line-clamp-2"
                          >
                            {item.notes}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 whitespace-nowrap">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-4 py-3.5">
                        <select
                          value={item.status}
                          disabled={updatingId === item.id}
                          onChange={(e) => handleStatusChange(item, e.target.value as DemoRequestStatus)}
                          className={`rounded-lg border px-2 py-1 text-xs font-semibold focus:outline-none ${cfg.badge}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="scheduled">Demo Scheduled</option>
                          <option value="completed">Completed</option>
                          <option value="archived">Archived</option>
                        </select>
                      </td>
                      <td className="px-4 py-3.5 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setSelectedLead(item)}
                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Details
                          </button>
                          <a
                            href={`mailto:${item.email}?subject=Flash Prepared Walkthrough for ${encodeURIComponent(item.organization)}`}
                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50"
                          >
                            Reply
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
            <div>
              Showing <span className="font-medium text-slate-900">{items.length}</span> of{' '}
              <span className="font-medium text-slate-900">{totalCount}</span> leads
            </div>
            <div className="flex items-center space-x-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-2">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lead Details Modal */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                  Demo Lead Details
                </span>
                <h3 className="text-lg font-bold text-slate-900">{selectedLead.name}</h3>
                <p className="text-xs text-slate-500">{selectedLead.organization}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLead(null)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-4 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <span className="text-[10px] uppercase font-semibold text-slate-400">Work Email</span>
                  <div className="mt-0.5 font-medium text-slate-800">
                    <a href={`mailto:${selectedLead.email}`} className="text-indigo-600 hover:underline">
                      {selectedLead.email}
                    </a>
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <span className="text-[10px] uppercase font-semibold text-slate-400">Role</span>
                  <div className="mt-0.5 font-medium text-slate-800">{selectedLead.role}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <span className="text-[10px] uppercase font-semibold text-slate-400">Company Size</span>
                  <div className="mt-0.5 font-medium text-slate-800">{selectedLead.organization_size}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <span className="text-[10px] uppercase font-semibold text-slate-400">Submitted</span>
                  <div className="mt-0.5 font-medium text-slate-800">{formatDate(selectedLead.created_at)}</div>
                </div>
              </div>

              <div>
                <span className="text-[10px] uppercase font-semibold text-slate-400">Interested In</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedLead.interests && selectedLead.interests.length > 0 ? (
                    selectedLead.interests.map((interest, i) => (
                      <span key={i} className="rounded-md bg-indigo-50 px-2 py-0.5 text-indigo-700 font-medium">
                        {interest}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400 italic">None selected</span>
                  )}
                </div>
              </div>

              {selectedLead.notes && (
                <div>
                  <span className="text-[10px] uppercase font-semibold text-slate-400">Notes / Audit Context</span>
                  <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {selectedLead.notes}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-600">Status:</span>
                  <select
                    value={selectedLead.status}
                    onChange={(e) => handleStatusChange(selectedLead, e.target.value as DemoRequestStatus)}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-800 text-xs"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="scheduled">Demo Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedLead(null)}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    Close
                  </button>
                  <a
                    href={`mailto:${selectedLead.email}?subject=Flash Prepared Walkthrough for ${encodeURIComponent(selectedLead.organization)}`}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 font-semibold text-white shadow-xs hover:bg-indigo-700"
                  >
                    Compose Email
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoRequestManagement;
